/**
 * findMenuUrl — pick the single best "menu" link from a restaurant page.
 *
 * Priority logic (editable in MENU_TYPES below):
 *   all-day  >  dinner  >  main/à-la-carte  >  generic "menu"  >  lunch  >  brunch ...
 *
 * Key detail: a link is classified by BOTH its anchor text AND its URL slug,
 * taking the highest-priority match of the two. This is what makes
 * Hummus Place work: the "All Day Menu" link points at /menu-lunch, so a
 * slug-only classifier would call it "lunch" and let Dinner win. Reading the
 * anchor text ("All Day") rescues it.
 *
 * Usage:
 *   const url = findMenuUrl(htmlString, 'https://www.hummusplace.com/');
 *   // or pass a DOM Document/Element instead of an HTML string:
 *   const url = findMenuUrl(document, location.href);
 *
 *   // Want to see the ranking / debug? Use the lower-level function:
 *   const ranked = findMenuCandidates(htmlString, baseUrl);
 *
 * fetchMenuUrl(siteUrl) fetches the page via the fetch-html Cloud Function
 * (a browser can't fetch an arbitrary third-party site directly — no CORS
 * headers on random restaurant sites) and runs findMenuUrl on the result.
 */

// Higher priority = more preferred. Tweak freely.
export const MENU_TYPES = [
  { type: "all-day", priority: 100, patterns: [/all[\s-]?day/] },
  { type: "dinner", priority: 90, patterns: [/dinner/, /supper/, /\bevening\b/] },
  {
    type: "main",
    priority: 80,
    patterns: [/\bmain(s)?\b/, /a[\s-]?la[\s-]?carte/, /full[\s-]?menu/, /food[\s-]?menu/],
  },
  {
    type: "menu",
    priority: 70,
    patterns: [/\bmenus?\b/, /\bmeny\b/, /\bcarte\b/, /speisekarte/],
  }, // generic
  { type: "lunch", priority: 60, patterns: [/lunch/] },
  { type: "brunch", priority: 55, patterns: [/brunch/] },
  { type: "breakfast", priority: 50, patterns: [/breakfast/, /\bmorning\b/] },
  {
    type: "drinks",
    priority: 30,
    patterns: [/drinks?/, /\bbar\b/, /cocktail/, /\bwine\b/, /\bbeer\b/, /beverage/],
  },
  { type: "dessert", priority: 25, patterns: [/dessert/, /\bsweets?\b/] },
  { type: "kids", priority: 20, patterns: [/\bkids?\b/, /children/] },
  { type: "catering", priority: 15, patterns: [/catering/, /\bevents?\b/, /part(y|ies)/] },
];

// Links that are never a menu, even if the word "menu" sneaks in.
const EXCLUDE = /(^|\/)(cart|checkout|account|login|sign[\s-]?in|contact)(\/|$|\?)/i;

function extractAnchors(input) {
  // DOM Document / Element
  if (input && typeof input.querySelectorAll === "function") {
    return Array.from(input.querySelectorAll("a[href]")).map((a) => ({
      href: a.getAttribute("href") || "",
      text: (a.textContent || "").replace(/\s+/g, " ").trim(),
      inNav: !!(a.closest && a.closest('nav, header, [role="navigation"]')),
    }));
  }
  // HTML string
  const anchors = [];
  const re = /<a\b[^>]*?href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
  let m;
  while ((m = re.exec(String(input))) !== null) {
    const text = m[2].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    anchors.push({ href: m[1], text, inNav: false });
  }
  return anchors;
}

function classify(text, slug) {
  const hay = `${text} ${slug}`.toLowerCase();
  let best = null;
  for (const t of MENU_TYPES) {
    if (t.patterns.some((p) => p.test(hay))) {
      if (!best || t.priority > best.priority) best = t;
    }
  }
  return best; // null if this link isn't menu-related at all
}

/** Returns all menu-link candidates, best first. */
export function findMenuCandidates(input, baseUrl) {
  const seen = new Set();
  const candidates = [];

  for (const a of extractAnchors(input)) {
    let url;
    try {
      url = new URL(a.href, baseUrl);
    } catch {
      continue; // javascript:, mailto:, malformed, etc.
    }
    if (!/^https?:$/.test(url.protocol)) continue;

    const slug = (url.pathname + url.search).toLowerCase();
    if (EXCLUDE.test(slug)) continue;

    const hit = classify(a.text, slug);
    if (!hit) continue; // not a menu link

    // Dedupe on path (ignore fragments/trailing slash).
    const key = url.origin + url.pathname.replace(/\/$/, "");
    if (seen.has(key)) continue;
    seen.add(key);

    // Score = type priority + small tie-breakers.
    let score = hit.priority;
    if (/menu|meny|carte/.test(slug)) score += 5; // slug looks menu-ish
    if (a.inNav) score += 3; // sitting in the nav
    if (url.origin !== new URL(baseUrl).origin) score -= 8; // off-site, mild demote

    candidates.push({
      url: url.href,
      type: hit.type,
      score,
      text: a.text,
    });
  }

  return candidates.sort((x, y) => y.score - x.score);
}

/** Returns just the best menu URL, or null if none found. */
export function findMenuUrl(input, baseUrl) {
  const ranked = findMenuCandidates(input, baseUrl);
  return ranked.length ? ranked[0].url : null;
}

const FETCH_HTML_ENDPOINT =
  "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/fetch-html";

/**
 * fetchMenuCandidates — give it only a site URL; it fetches the page via the
 * fetch-html proxy and returns the ranked candidate list (best first).
 */
export async function fetchMenuCandidates(siteUrl) {
  const res = await fetch(FETCH_HTML_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url: siteUrl }),
  });

  const data = await res.json();

  if (!res.ok || !data.success) {
    throw new Error(data.error || `Fetch failed: ${res.status}`);
  }

  return findMenuCandidates(data.html, data.finalUrl || siteUrl);
}

/** Returns just the best menu URL (or null), fetching via the proxy. */
export async function fetchMenuUrl(siteUrl) {
  const ranked = await fetchMenuCandidates(siteUrl);
  return ranked.length ? ranked[0].url : null;
}
